"use client";
import { z } from "zod";
import { FC, Fragment, useEffect } from "react";
import { ImageZoomPan } from "./ImageZoomPan";
import { linearTiming, TransitionSeries } from "@remotion/transitions";
import { fade } from "@remotion/transitions/fade";
import {
  useVideoConfig,
  Audio,
  AbsoluteFill,
  OffthreadVideo,
  CalculateMetadataFunction,
} from "remotion";
import { getAudioDurationInSeconds } from "@remotion/media-utils";

import { loadFont } from "./utils/fonts";
import { CaptionDisplay } from "./captions/CaptionDisplay";
import { SlideshowPropsSchema } from "./schemas";

export const calcSlideshowMetadata: CalculateMetadataFunction<
  z.infer<typeof SlideshowPropsSchema>
> = async ({
  props,
}): Promise<{
  props: z.infer<typeof SlideshowPropsSchema>;
  durationInFrames: number;
  fps: number;
  width: number;
  height: number;
}> => {
  const audioDurations = await Promise.all(
    props.audioTracks.map((track) => getAudioDurationInSeconds(track.path))
  );

  const totalDuration = Math.max(
    ...props.clips.map((clip) => clip.startTime + clip.duration),
    ...audioDurations
  );

  return {
    props: { ...props },
    durationInFrames: Math.round(props.videoConfig.fps * totalDuration),
    fps: props.videoConfig.fps,
    width: props.videoConfig.width,
    height: props.videoConfig.height,
  };
};

export const SlideshowComposition: FC<z.infer<typeof SlideshowPropsSchema>> = ({
  clips,
  audioTracks,
  captionLines = [],
  captionStyles,
}) => {
  useEffect(() => {
    loadFont(captionStyles.fontFamily);
  }, [captionStyles.fontFamily]);

  const { fps } = useVideoConfig();

  return (
    <>
      <TransitionSeries>
        {clips.map((clip, index) => (
          <Fragment key={clip.path}>
            <TransitionSeries.Sequence
              durationInFrames={Math.round(clip.duration * fps)}
              key={clip.path}
            >
              <AbsoluteFill>
                {clip.type === "video" ? (
                  <OffthreadVideo
                    pauseWhenBuffering={true}
                    src={clip.path}
                    muted={true}
                    style={clip.style}
                  />
                ) : (
                  <ImageZoomPan clip={clip} />
                )}
              </AbsoluteFill>
            </TransitionSeries.Sequence>
            {index < clips.length - 1 && (
              <TransitionSeries.Transition
                timing={linearTiming({
                  durationInFrames: Math.round(
                    clips[index].transition.duration * fps
                  ),
                })}
                presentation={fade()}
              />
            )}
          </Fragment>
        ))}
      </TransitionSeries>
      <AbsoluteFill>
        <CaptionDisplay
          captionLines={captionLines}
          captionStyles={captionStyles}
        />
      </AbsoluteFill>
      {audioTracks.map((track) => (
        <Audio key={track.path} src={track.path} volume={track.volume} />
      ))}
    </>
  );
};
